import { useNavigate } from 'react-router-dom'
import { Row, Col } from 'react-bootstrap'
import { IPageProps, IRootState, IDealerChangedEvent } from 'types'
import { AddButton, FormDropDownList, FormInput, PageHeader, ReturnButton } from 'components'
import DealerSelect from 'components/Dealers/DealerSelect'
import { useRef, useState } from 'react'
import { Form, FormElement, Field, FieldWrapper } from '@progress/kendo-react-form'
import { ColorPicker, ColorPickerChangeEvent } from '@progress/kendo-react-inputs'
import { Label } from '@progress/kendo-react-labels'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'state/store'
import { V1ApiIntegrationsClient } from 'core/http-common'
import { CreateAudioEyeSiteDto } from 'generated/v1/v1IntegrationsApi'
import toast from 'core/toast'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { RequiredValidator } from 'core/Validators'
import { toHexColor } from 'core/helpers'

interface IStringDropDownValue {
    id: string
    text: string
}

// Real values from the AudioEye vendor API's config.ctaLocation/toolbarTheme fields (see AddAudioEye.aspx)
const locationOptions: IStringDropDownValue[] = [
    { id: 'right-lower', text: 'Lower Right' },
    { id: 'right-mid-lower', text: 'Lower Mid Right' },
    { id: 'right-middle', text: 'Middle Right' },
    { id: 'right-mid-upper', text: 'Upper Mid Right' },
    { id: 'right-upper', text: 'Upper Right' },
    { id: 'left-lower', text: 'Lower Left' },
]

const toolbarThemeOptions: IStringDropDownValue[] = [
    { id: 'light', text: 'Light' },
    { id: 'dark', text: 'Dark' },
]

const AudioEyeAddPage = ({ title }: IPageProps) => {

    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const redirectUrl = '/integrations/audioeye'
    const loading = useSelector((state: IRootState) => state.app.loading)

    const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(undefined)
    const [hexColor, setHexColor] = useState<string>('#000000')
    const [hasPremiumAda, setHasPremiumAda] = useState<boolean | undefined>(undefined)
    const [checkingPremiumAda, setCheckingPremiumAda] = useState(false)
    // DealerSelect fires onDealerChanged twice per selection (its ComboBox's onChange, then
    // onClose) - dedupe here so the Premium ADA check/toast doesn't run twice for one selection.
    const lastCheckedDealerId = useRef<number | undefined>(undefined)
    // Forces DealerSelect to fully remount (clearing its internal selection) when we reject a
    // dealer for lacking Premium ADA - it has no other way to be cleared from the parent.
    const [dealerFieldKey, setDealerFieldKey] = useState(0)

    const dealerIsValid = !!selectedDealerId && hasPremiumAda === true

    const handleDealerChange = async (event: IDealerChangedEvent) => {
        if (!event.dealerId || event.dealerId === lastCheckedDealerId.current) {
            setSelectedDealerId(event.dealerId)
            return
        }

        lastCheckedDealerId.current = event.dealerId
        setSelectedDealerId(event.dealerId)
        setHasPremiumAda(undefined)

        try {
            setCheckingPremiumAda(true)
            const result = await V1ApiIntegrationsClient.hasPremiumAda(event.dealerId)

            if (!result) {
                toast.error('This dealer does not have an active Premium ADA product. AudioEye cannot be provisioned until they have one.')
                setSelectedDealerId(undefined)
                setHasPremiumAda(undefined)
                lastCheckedDealerId.current = undefined
                setDealerFieldKey(key => key + 1)
                return
            }

            setHasPremiumAda(true)
        } catch (e) {
            toast.responseErrors(e)
        } finally {
            setCheckingPremiumAda(false)
        }
    }

    const handleDealerClear = () => {
        setSelectedDealerId(undefined)
        setHasPremiumAda(undefined)
        lastCheckedDealerId.current = undefined
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSubmit = async (values: any) => {
        if (!selectedDealerId) return

        try {
            await dispatch(startLoadingAction())

            const dto = new CreateAudioEyeSiteDto({
                dealerId: selectedDealerId,
                websiteUrl: values.websiteUrl,
                location: values.location?.id ?? values.location,
                toolbarTheme: values.toolbarTheme?.id ?? values.toolbarTheme,
                color: hexColor
            })

            await V1ApiIntegrationsClient.createSite(dto)
            toast.success('AudioEye provisioned successfully. A case has been created with the install instructions.')
            navigate(redirectUrl)
        } catch (e) {
            toast.responseErrors(e)
        } finally {
            await dispatch(endLoadingAction())
        }
    }

    return (
        <div id='AudioEyeAddPage'>
            <PageHeader title={title}>
                <ReturnButton redirectUrl={redirectUrl} />
            </PageHeader>

            <Form
                onSubmit={handleSubmit}
                ignoreModified={true}
                initialValues={{
                    location: locationOptions[0],
                    toolbarTheme: toolbarThemeOptions[0]
                }}
                render={(formRenderProps) => (
                    <FormElement>
                        <div className='bg-light border p-3 mb-3'>
                            <div className='bg-white border p-3'>
                                <Row>
                                    <Col lg={6} md={6}>
                                        <Field
                                            key={dealerFieldKey}
                                            name='dealer'
                                            label='Dealer:'
                                            component={DealerSelect}
                                            dealerId={selectedDealerId ?? 0}
                                            onDealerChanged={handleDealerChange}
                                            onDealerCleared={handleDealerClear}
                                            required={true}
                                        />
                                    </Col>
                                    <Col lg={6} md={6}>
                                        <Field
                                            name='websiteUrl'
                                            label='Website Url:'
                                            component={FormInput}
                                            className='form-control'
                                            required
                                            validator={RequiredValidator}
                                            disabled={!dealerIsValid}
                                        />
                                    </Col>
                                    <Col lg={6} md={6}>
                                        <Field
                                            name='location'
                                            label='Location on Website:'
                                            component={FormDropDownList}
                                            data={locationOptions}
                                            textField='text'
                                            dataItemKey='id'
                                            disabled={!dealerIsValid}
                                        />
                                    </Col>
                                    <Col lg={6} md={6}>
                                        <Field
                                            name='toolbarTheme'
                                            label='Toolbar Theme:'
                                            component={FormDropDownList}
                                            data={toolbarThemeOptions}
                                            textField='text'
                                            dataItemKey='id'
                                            disabled={!dealerIsValid}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={6} md={6}>
                                        <FieldWrapper>
                                            <Label>Hex Color for Icon:</Label>
                                            <ColorPicker
                                                value={hexColor}
                                                onChange={(e: ColorPickerChangeEvent) => setHexColor(toHexColor(e.value as string))}
                                                view='palette'
                                                disabled={!dealerIsValid}
                                            />
                                        </FieldWrapper>
                                    </Col>
                                </Row>
                                <Row className='mt-3'>
                                    <Col>
                                        <AddButton
                                            type='submit'
                                            className='btn-lg btn-primary btnWidth'
                                            disabled={!dealerIsValid || checkingPremiumAda || !formRenderProps.allowSubmit || loading}>
                                            Save
                                        </AddButton>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </FormElement>
                )}
            />
        </div>
    )
}

export default AudioEyeAddPage
